// @ts-nocheck

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Download, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";

interface ClickLog {
  id: string;
  timestamp: string;
  email_template_id: string | null;
  campaign_id: string | null;
  clicked_url: string;
  user_id: string | null;
  profile_id: string | null;
  ip_address: string | null;
  user_agent: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  source_type: string;
  metadata: any;
  created_at: string;
}

const CommunicationClicksTab: React.FC = () => {
  const [clickLogs, setClickLogs] = useState<ClickLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    sourceType: 'all',
    searchUrl: ''
  });

  const pageSize = 50;

  useEffect(() => {
    fetchClickLogs();
  }, [currentPage, filters]);

  const fetchClickLogs = async () => {
    setIsLoading(true);
    try {
      let query = (supabase as any)
        .from('communication_click_logs')
        .select('*', { count: 'exact' })
        .order('timestamp', { ascending: false })
        .range((currentPage - 1) * pageSize, currentPage * pageSize - 1);

      // Apply filters
      if (filters.startDate) {
        query = query.gte('timestamp', filters.startDate);
      }
      if (filters.endDate) {
        query = query.lte('timestamp', filters.endDate + 'T23:59:59');
      }
      if (filters.sourceType) {
        query = query.eq('source_type', filters.sourceType);
      }
      if (filters.searchUrl) {
        query = query.ilike('clicked_url', `%${filters.searchUrl}%`);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      setClickLogs((data as any) || []);
      setTotalPages(Math.ceil((count || 0) / pageSize));
    } catch (error) {
      console.error('Error fetching click logs:', error);
      toast.error('Failed to fetch click logs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      sourceType: 'all',
      searchUrl: ''
    });
    setCurrentPage(1);
  };

  const exportToCSV = async () => {
    try {
      let query = (supabase as any)
        .from('communication_click_logs')
        .select('*')
        .order('timestamp', { ascending: false });

      // Apply same filters for export
      if (filters.startDate) {
        query = query.gte('timestamp', filters.startDate);
      }
      if (filters.endDate) {
        query = query.lte('timestamp', filters.endDate + 'T23:59:59');
      }
      if (filters.sourceType) {
        query = query.eq('source_type', filters.sourceType);
      }
      if (filters.searchUrl) {
        query = query.ilike('clicked_url', `%${filters.searchUrl}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      if (!data || data.length === 0) {
        toast.error('No data to export');
        return;
      }

      // Create CSV content
      const headers = [
        'Timestamp',
        'Source Type',
        'Clicked URL',
        'User ID',
        'Profile ID',
        'IP Address',
        'UTM Source',
        'UTM Campaign',
        'UTM Medium',
        'UTM Content',
        'UTM Term'
      ];

      const csvContent = [
        headers.join(','),
        ...data.map((log: any) => [
          format(new Date(log.timestamp), 'yyyy-MM-dd HH:mm:ss'),
          log.source_type,
          `"${log.clicked_url}"`,
          log.user_id || '',
          log.profile_id || '',
          log.ip_address || '',
          log.utm_source || '',
          log.utm_campaign || '',
          log.utm_medium || '',
          log.utm_content || '',
          log.utm_term || ''
        ].join(','))
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `communication-clicks-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success('Click logs exported successfully');
    } catch (error) {
      console.error('Error exporting click logs:', error);
      toast.error('Failed to export click logs');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Clicks & Track-throughs</CardTitle>
            <Button onClick={exportToCSV} variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="sourceType">Source Type</Label>
              <Select value={filters.sourceType} onValueChange={(value) => handleFilterChange('sourceType', value === 'all' ? '' : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="banner">Banner</SelectItem>
                  <SelectItem value="notification">Notification</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="searchUrl">Search URL</Label>
              <Input
                id="searchUrl"
                placeholder="Filter by URL..."
                value={filters.searchUrl}
                onChange={(e) => handleFilterChange('searchUrl', e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end mb-4">
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Clicked URL</TableHead>
                  <TableHead>User/Profile</TableHead>
                  <TableHead>UTM Data</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      Loading click logs...
                    </TableCell>
                  </TableRow>
                ) : clickLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No click logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  clickLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <div className="text-sm">
                          <div>{format(new Date(log.timestamp), 'MMM dd, yyyy')}</div>
                          <div className="text-muted-foreground">{format(new Date(log.timestamp), 'HH:mm:ss')}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="capitalize text-sm font-medium">
                          {log.source_type}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs truncate text-sm" title={log.clicked_url}>
                          {log.clicked_url}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {log.profile_id && <div>Profile: {log.profile_id.substring(0, 8)}...</div>}
                          {log.user_id && <div className="text-muted-foreground">User: {log.user_id.substring(0, 8)}...</div>}
                          {!log.profile_id && !log.user_id && <span className="text-muted-foreground">Anonymous</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm space-y-1">
                          {log.utm_source && <div>Source: {log.utm_source}</div>}
                          {log.utm_campaign && <div>Campaign: {log.utm_campaign}</div>}
                          {log.utm_medium && <div>Medium: {log.utm_medium}</div>}
                          {!log.utm_source && !log.utm_campaign && !log.utm_medium && (
                            <span className="text-muted-foreground">No UTM data</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {log.ip_address || 'N/A'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" asChild>
                          <a href={log.clicked_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <Pagination>
                <PaginationContent>
                  {currentPage > 1 && (
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        className="cursor-pointer"
                      />
                    </PaginationItem>
                  )}
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                    return (
                      <PaginationItem key={pageNum}>
                        <PaginationLink
                          onClick={() => setCurrentPage(pageNum)}
                          isActive={currentPage === pageNum}
                          className="cursor-pointer"
                        >
                          {pageNum}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  
                  {currentPage < totalPages && (
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        className="cursor-pointer"
                      />
                    </PaginationItem>
                  )}
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CommunicationClicksTab;
